import React, { useEffect, useState } from 'react';

import { useLanguage } from '../context/LanguageContext';
import { apiRequest } from '../lib/api';

const defaultLead = {
  name: '',
  email: '',
  phone: '',
  business_name: '',
  reason_for_consultation: '',
};

const ChatWidget = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [leadForm, setLeadForm] = useState(defaultLead);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initializeChat = async () => {
      try {
        const settings = await apiRequest('/api/settings/public');
        setChatEnabled(settings.chatbot_enabled);
        if (!settings.chatbot_enabled) {
          return;
        }

        const storedSessionId = localStorage.getItem('illumidev_chat_session_id');
        if (storedSessionId) {
          setSessionId(storedSessionId);
          setMessages([
            {
              role: 'assistant',
              text: language === 'es'
                ? 'Estoy listo para ayudarte con precios, tiempos, servicios o capturar tu proyecto.'
                : 'I am ready to help with pricing, timelines, services, or capturing your project details.',
            },
          ]);
        } else {
          const session = await apiRequest(`/api/chat/session?language=${language}`, { method: 'POST' });
          localStorage.setItem('illumidev_chat_session_id', session.session_id);
          setSessionId(session.session_id);
          setMessages([{ role: 'assistant', text: session.welcome_message }]);
        }
      } catch (error) {
        setChatEnabled(false);
      } finally {
        setIsReady(true);
      }
    };

    initializeChat();
  }, [language]);

  const sendMessage = async (text, lead = undefined) => {
    if (!text.trim() || !chatEnabled) {
      return;
    }

    const sanitizedLead = lead
      ? Object.fromEntries(Object.entries(lead).filter(([, value]) => String(value || '').trim()))
      : undefined;

    setLoading(true);
    setMessages((currentMessages) => [...currentMessages, { role: 'user', text }]);
    setMessageInput('');

    try {
      const data = await apiRequest('/api/chat/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text, language, lead: sanitizedLead }),
      });

      if (data.session_id && !sessionId) {
        localStorage.setItem('illumidev_chat_session_id', data.session_id);
        setSessionId(data.session_id);
      }

      setMessages((currentMessages) => [...currentMessages, { role: 'assistant', text: data.response }]);
      if (sanitizedLead && Object.keys(sanitizedLead).length) {
        setLeadForm(defaultLead);
        setShowLeadForm(false);
      }
    } catch (error) {
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          role: 'assistant',
          text: language === 'es' ? 'Hubo un problema temporal. Intenta de nuevo en un momento.' : 'There was a temporary issue. Please try again shortly.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const content = {
    es: {
      title: 'AI Concierge',
      subtitle: 'Resuelve dudas y captura tu proyecto',
      placeholder: 'Escribe tu pregunta...',
      send: 'Enviar',
      details: 'Compartir detalles',
      detailsButton: 'Guardar detalles',
      leadPrompt: 'Comparte tus datos para que pueda dejar tu lead listo.',
      fields: {
        name: 'Nombre',
        email: 'Email',
        phone: 'Teléfono',
        business_name: 'Negocio',
        reason_for_consultation: 'Qué necesitas',
      },
    },
    en: {
      title: 'AI Concierge',
      subtitle: 'Answer questions and capture your project',
      placeholder: 'Write your question...',
      send: 'Send',
      details: 'Share details',
      detailsButton: 'Save details',
      leadPrompt: 'Share your details so I can save your lead for follow-up.',
      fields: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        business_name: 'Business',
        reason_for_consultation: 'What do you need',
      },
    },
  };

  const t = content[language];

  if (!isReady || !chatEnabled) {
    return null;
  }

  return (
    <div className="chat-widget-shell" data-testid="chat-widget-shell">
      {isOpen && (
        <div className="chat-widget-panel" data-testid="chat-widget-panel">
          <div className="chat-widget-header">
            <div>
              <strong data-testid="chat-widget-title">{t.title}</strong>
              <p data-testid="chat-widget-subtitle">{t.subtitle}</p>
            </div>
            <button className="chat-close-button" onClick={() => setIsOpen(false)} data-testid="chat-widget-close-button">×</button>
          </div>

          <div className="chat-messages" data-testid="chat-widget-messages">
            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={`chat-message ${message.role}`} data-testid={`chat-message-${message.role}-${index}`}>
                <p>{message.text}</p>
              </div>
            ))}
            {loading && <div className="chat-message assistant"><p>...</p></div>}
          </div>

          <div className="chat-lead-toggle-row">
            <button className="btn btn-outline" onClick={() => setShowLeadForm((current) => !current)} data-testid="chat-widget-lead-toggle-button">
              {t.details}
            </button>
          </div>

          {showLeadForm && (
            <div className="chat-lead-form" data-testid="chat-widget-lead-form">
              <p>{t.leadPrompt}</p>
              {Object.entries(t.fields).map(([key, label]) => (
                <input
                  key={key}
                  type={key === 'email' ? 'email' : 'text'}
                  placeholder={label}
                  value={leadForm[key]}
                  onChange={(event) => setLeadForm((currentLead) => ({ ...currentLead, [key]: event.target.value }))}
                  data-testid={`chat-widget-lead-${key}-input`}
                />
              ))}
              <button
                className="btn btn-primary"
                onClick={() => sendMessage(language === 'es' ? 'Quiero compartir los detalles de mi proyecto.' : 'I want to share my project details.', leadForm)}
                disabled={loading}
                data-testid="chat-widget-lead-save-button"
              >
                {t.detailsButton}
              </button>
            </div>
          )}

          <div className="chat-input-row">
            <textarea
              rows="2"
              value={messageInput}
              onChange={(event) => setMessageInput(event.target.value)}
              placeholder={t.placeholder}
              data-testid="chat-widget-message-input"
            />
            <button className="btn btn-primary" onClick={() => sendMessage(messageInput, Object.values(leadForm).some(Boolean) ? leadForm : undefined)} disabled={loading} data-testid="chat-widget-send-button">
              {t.send}
            </button>
          </div>
        </div>
      )}

      <button className="chat-widget-trigger" onClick={() => setIsOpen((current) => !current)} data-testid="chat-widget-trigger-button">
        {isOpen ? '—' : 'Chat'}
      </button>
    </div>
  );
};

export default ChatWidget;